import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Chip,
    Autocomplete,
    Typography,
    CircularProgress,
    Card,
    CardContent,
    Avatar,
    Paper,
    IconButton,
    LinearProgress,
    Fade,
    Zoom,
    Alert,
    Tooltip,
    Stack,
} from '@mui/material';
import {
    Article as ArticleIcon,
    Save as SaveIcon,
    CloudUpload as CloudUploadIcon,
    Image as ImageIcon,
    Tag as TagIcon,
    Description as DescriptionIcon,
    Title as TitleIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import Editor from '../../components/common/Editor';
import ModernButton from '../../components/common/ModernButton';
import ModernTextField from '../../components/common/ModernTextField';

const BlogForm = ({ initialData = {}, onSubmit, loading }) => {
    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [metaTags, setMetaTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const editorRef = useRef();
    const [content, setContent] = useState('');

    // Initialize form fields when initialData changes
    useEffect(() => {
        setTitle(initialData.title || '');
        setSlug(initialData.slug || '');
        const tags = initialData.metaTags
            ? Array.isArray(initialData.metaTags)
                ? initialData.metaTags
                : initialData.metaTags.split(',').map((t) => t.trim()).filter(Boolean)
            : [];
        setMetaTags(tags);
        setTagInput('');
        setMetaDescription(initialData.metaDescription || '');
        setPreviews(initialData.photos || []);
        setFiles([]);
        setContent(initialData.content || '');
    }, [initialData]);

    // Auto-generate slug if it's a new post
    useEffect(() => {
        if (!initialData._id) {
            const gen = title
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9\-]/g, '');
            setSlug(gen);
        }
    }, [title, initialData._id]);

    // Cleanup blob URLs
    useEffect(() => {
        return () => {
            previews
                .filter((p) => p.startsWith('blob:'))
                .forEach((url) => URL.revokeObjectURL(url));
        };
    }, [previews]);

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        setFiles(selected);
        setPreviews(selected.map((f) => URL.createObjectURL(f)));
    };

    const handleRemovePreview = (idx) => {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
        setPreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    const addTagsFromInput = (input = tagInput) => {
        const parts = input
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
        if (parts.length) {
            setMetaTags((prev) => Array.from(new Set([...prev, ...parts])));
        }
        setTagInput('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const contentHTML = editorRef.current?.getHTML() || '';
        const fd = new FormData();
        fd.append('title', title);
        fd.append('slug', slug);
        fd.append('metaTags', metaTags.join(', '));
        fd.append('metaDescription', metaDescription);
        fd.append('content', contentHTML);
        files.forEach((file) => fd.append('photos', file));
        onSubmit(fd);
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
                {/* Left Column - Main Content */}
                <Grid item xs={12} md={8}>
                    {/* Basic Information Card */}
                    <Card sx={{ mb: 3, borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ backgroundColor: 'primary.main', width: 32, height: 32, mr: 2 }}>
                                    <TitleIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Basic Information
                                </Typography>
                            </Box>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <ModernTextField
                                        label="Blog Title"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        fullWidth
                                        required
                                        placeholder="Enter an engaging title for your blog post"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ModernTextField
                                        label="URL Slug"
                                        value={slug}
                                        onChange={(e) => setSlug(e.target.value)}
                                        fullWidth
                                        required
                                        helperText="URL-friendly version of the title"
                                        placeholder="url-friendly-slug"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Content Editor Card */}
                    <Card sx={{ mb: 3, borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ backgroundColor: 'info.main', width: 32, height: 32, mr: 2 }}>
                                    <ArticleIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Blog Content
                                </Typography>
                            </Box>
                            
                            <Paper sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                                <Editor
                                    key={initialData._id || 'new'}
                                    ref={editorRef}
                                    defaultValue={content}
                                />
                            </Paper>
                        </CardContent>
                    </Card>

                    {/* SEO & Meta Information Card */}
                    <Card sx={{ mb: 3, borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ backgroundColor: 'success.main', width: 32, height: 32, mr: 2 }}>
                                    <TagIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    SEO & Meta Information
                                </Typography>
                            </Box>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        options={[]}
                                        value={metaTags}
                                        inputValue={tagInput}
                                        onInputChange={(e, val, reason) => {
                                            if (reason === 'input') {
                                                if (val.includes(',')) {
                                                    addTagsFromInput(val);
                                                } else {
                                                    setTagInput(val);
                                                }
                                            }
                                        }}
                                        onChange={(_, value) => setMetaTags(value)}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="outlined"
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                    key={option}
                                                    color="primary"
                                                />
                                            ))
                                        }
                                        renderInput={(params) => (
                                            <ModernTextField
                                                {...params}
                                                label="Meta Tags"
                                                placeholder="Add tags separated by commas"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ',') {
                                                        e.preventDefault();
                                                        addTagsFromInput();
                                                    }
                                                }}
                                            />
                                        )}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <ModernTextField
                                        label="Meta Description"
                                        value={metaDescription}
                                        onChange={(e) => setMetaDescription(e.target.value)}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="Brief description for search engines (150-160 characters recommended)"
                                        helperText={`${metaDescription.length}/160 characters`}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Image Upload Card */}
                    <Card sx={{ mb: 3, borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ backgroundColor: 'warning.main', width: 32, height: 32, mr: 2 }}>
                                    <ImageIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Featured Images
                                </Typography>
                            </Box>
                            
                            <Box sx={{ mb: 3 }}>
                                <ModernButton
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUploadIcon />}
                                >
                                    Upload Images
                                    <input
                                        type="file"
                                        hidden
                                        multiple
                                        accept="image/*"
                                        onChange={handleFileChange}
                                    />
                                </ModernButton>
                            </Box>

                            {/* Image Previews */}
                            {previews.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {previews.map((url, i) => (
                                        <Card key={i} sx={{ width: 120, height: 120, position: 'relative' }}>
                                            <Box
                                                component="img"
                                                src={url}
                                                alt={`preview-${i}`}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: 2,
                                                }}
                                            />
                                            <IconButton
                                                onClick={() => handleRemovePreview(i)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    backgroundColor: 'error.main',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: 'error.dark',
                                                    },
                                                    width: 24,
                                                    height: 24,
                                                }}
                                            >
                                                ×
                                            </IconButton>
                                        </Card>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column - Actions & Preview */}
                <Grid item xs={12} md={4}>
                    {/* Blog Preview Card */}
                    <Card sx={{ mb: 3, borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ backgroundColor: 'info.main', width: 32, height: 32, mr: 2 }}>
                                    <DescriptionIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Blog Preview
                                </Typography>
                            </Box>
                            
                            <Box sx={{ space: 2 }}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                        Title
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {title || 'No title set'}
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                        Slug
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                                        {slug || 'No slug set'}
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                        Tags ({metaTags.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {metaTags.length > 0 ? (
                                            metaTags.slice(0, 3).map((tag) => (
                                                <Chip
                                                    key={tag}
                                                    label={tag}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                No tags added
                                            </Typography>
                                        )}
                                        {metaTags.length > 3 && (
                                            <Chip
                                                label={`+${metaTags.length - 3}`}
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </Box>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                        Meta Description
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {metaDescription || 'No meta description set'}
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                        Images
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {previews.length} image{previews.length !== 1 ? 's' : ''} uploaded
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Action Buttons Card */}
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ModernButton
                                    type="submit"
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                                    fullWidth
                                    disabled={loading}
                                >
                                    {initialData._id ? 'Update Blog Post' : 'Create Blog Post'}
                                </ModernButton>
                                
                                {initialData._id && (
                                    <ModernButton
                                        onClick={() => {
                                            setTitle('');
                                            setSlug('');
                                            setMetaTags([]);
                                            setMetaDescription('');
                                            setPreviews([]);
                                            setFiles([]);
                                            setContent('');
                                        }}
                                        variant="outlined"
                                        fullWidth
                                    >
                                        Clear Form
                                    </ModernButton>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BlogForm;
